import { useState } from "react";
import {
    Dialog,
    DialogContent,
    TextField,
    Button,
    Stack,
    Typography,
    Alert,
    CircularProgress,
    Link,
    InputAdornment,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import Logo from "./Logo";

export default function ResetPasswordDialog({ open, onClose, onSwitchToLogin }) {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await sendPasswordResetEmail(getAuth(), email);
            setSent(true);
        } catch (err) {
            if (err.code === "auth/user-not-found") {
                setError(t("auth.resetNoAccount"));
            } else {
                setError(t("auth.genericError"));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSent(false);
        setEmail("");
        setError("");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
            <DialogContent>
                <Stack alignItems="center" sx={{ mb: 3 }}>
                    <Logo size={48} />
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 1.5 }}>
                        {t("auth.resetTitle")}
                    </Typography>
                    <Typography color="text.secondary" variant="body2" sx={{ textAlign: "center" }}>
                        {t("auth.resetSubtitle")}
                    </Typography>
                </Stack>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {sent ? (
                    <Stack alignItems="center" spacing={2}>
                        <Alert severity="success">
                            {t("auth.resetSentPrefix")} <strong>{email}</strong>{t("auth.resetSentSuffix")}
                        </Alert>
                        <Button
                            variant="contained"
                            onClick={() => { handleClose(); onSwitchToLogin(); }}
                            sx={{ borderRadius: 2, fontWeight: 700 }}
                        >
                            {t("auth.backToLogin")}
                        </Button>
                    </Stack>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label={t("auth.email")}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Icon icon="mdi:email-outline" width={20} color="#919EAB" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                type="submit"
                                size="large"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon icon="mdi:send" />}
                                sx={{ py: 1.3, borderRadius: 2, fontWeight: 700 }}
                            >
                                {loading ? t("auth.sending") : t("auth.sendLink")}
                            </Button>
                        </Stack>
                    </form>
                )}

                {!sent && (
                    <Typography sx={{ mt: 2.5, textAlign: "center" }} variant="body2">
                        <Link component="button" onClick={onSwitchToLogin} sx={{ fontWeight: 700 }}>
                            {t("auth.backToLogin")}
                        </Link>
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
}
